<template>
  <slot name="title" :contacts-count="paginatorInfo?.total" :refetch="refetch"></slot>

  <div class="card card-white">
    <RecruitersList
      :additional-columns="columns"
      :entries="entries"
      :loading="loading"
      :paginator-info="paginatorInfo"
      :multi-select="multiSelect"
      :pre-selected-rows="preSelectedRows"
      @row-click="emit('row-click', $event)"
      @rows-selected="emit('rows-selected', $event)"
      @search="search"
    >
      <template v-for="name in Object.keys($slots).filter(filterSlots)" #[name]="slotProps">
        <slot :name="name" v-bind="slotProps || {}"></slot>
      </template>
      <template #control-bar>
        <RecruitersControlBar
          :default-page-length="DEFAULT_PAGE_LENGTH"
          @search="searchInput"
          @update-filters="updateFilters"
          @update-page-length="updatePageLength"
        />
      </template>
    </RecruitersList>
    <!-- Use this slot as a provider, allowing you to perform actions needing to refresh after-->
    <slot name="actions" :refetch="refetch"></slot>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, Ref } from 'vue'
import { useDebounceFn } from '@vueuse/core'
import { stringToEnum } from '@/utils/tools'
import RecruitersControlBar from '@/components/Contacts/CompanyRecruiter/RecruitersControlBar.vue'
import RecruitersList from '@/components/Contacts/CompanyRecruiter/RecruitersList.vue'
import { RecruitersList as CompanyRecruitersList } from '@/components/Contacts/CompanyRecruiter/queries/Recruiters'
import useRecruitersList, {
  RecruiterListVariables,
} from '@/components/Contacts/CompanyRecruiter/composables/recruitersList'
import { Recruiter } from '@/components/Job/hiring-types'
import { SortOrder } from '@/gql/graphql'

interface Props {
  multiSelect?: boolean
  preSelectedRecruiters?: Recruiter[]
  additionalColumns?: object[]
}

const props = withDefaults(defineProps<Props>(), {
  multiSelect: false,
  preSelectedRecruiters: () => [],
  additionalColumns: () => [],
})

const emit = defineEmits(['rows-selected', 'row-click', 'search', 'tab-toggled'])

const DEFAULT_PAGE_LENGTH = 50

const columnSortMapping = {
  name: 'RECRUITER_NAME',
  market: 'RECRUITER_MARKET',
  fee: 'RECRUITER_FEE',
  ownershipDays: 'RECRUITER_OWNERSHIP_DAYS',
  status: 'RECRUITER_STATUS',
}

const columns = computed(() => props.additionalColumns)

const query = computed(() => CompanyRecruitersList)

const queryVariables: Ref<RecruiterListVariables> = ref({
  accounts: [currentAccount.globalId],
  page: 1,
  perPage: DEFAULT_PAGE_LENGTH,
  search: null,
  orderBy: [
    {
      column: columnSortMapping.name,
      order: SortOrder.Asc,
    },
  ],
} as RecruiterListVariables)

const {
  recruiters: entries,
  rawResult,
  loading,
  refetch,
} = useRecruitersList(query, queryVariables)

const preSelectedRows = computed(() =>
  entries.value.filter((entry) =>
    props.preSelectedRecruiters.find((recruiter) => recruiter.recruiterId === entry.recruiterId),
  ),
)

const paginatorInfo = computed(() => rawResult.value?.companyRecruiters?.paginatorInfo)

const updateFilters = (filters) => {
  queryVariables.value = {
    ...queryVariables.value,
    ...filters,
    // Reset page when filtering so that resulting records will display
    page: 0,
  }
}

const updatePageLength = (value) => {
  queryVariables.value.perPage = value
}

const searchInput = useDebounceFn((search) => {
  queryVariables.value = {
    ...queryVariables.value,
    // Reset page when searching so that resulting records will display
    page: 0,
    search,
  }
}, 600)

const search = (values) => {
  const orderBy: object[] = []
  for (const key in values.order) {
    if (values.order[key]) {
      orderBy.push({
        column: columnSortMapping[key],
        order: stringToEnum(values.order[key]),
      })
    }
  }
  queryVariables.value = {
    ...queryVariables.value,
    page: values.page + 1,
    perPage: values.perPage,
    orderBy,
  }
}

const filterSlots = (slotName: string) => !['control-bar'].includes(slotName)
</script>

<style lang="scss" scoped>
.direct-hire-button {
  @apply tw-mr-4;
}
</style>
